/**
 * First-time password / password-reset API client.
 *
 * Both calls are wired to the BFF:
 *   • `validateResetToken` → `GET /auth/set-password/:token/status`
 *   • `setPassword`        → `POST /auth/set-password/:token`,
 *                            body `{ new_password }`.
 *
 * The literal token "expired" is preserved as a QA shortcut so
 * designers can preview the link-expired card without hitting the
 * server.
 */

import api from "./api";

const VALIDATION_DELAY_MS = 400;

// Simulate a network round-trip so the UI shows its validating spinner.
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const passwordService = {
  /**
   * Validate the token embedded in the magic-link slug.
   * `GET /auth/set-password/:token/status`.
   *
   * BE response shape (200 in all three verdicts):
   *   • `{ valid: true,  issued_at, expires_at, now }`
   *   • `{ valid: false, message: "Token has already been used", … }`
   *   • `{ valid: false, message: "Invalid token", … }`
   *
   * Normalised to `{ status: "valid" | "used" | "expired" }`:
   *   • `valid: true`                    → "valid"
   *   • `valid: false` + msg "already…used" → "used" (sign-in CTA)
   *   • `valid: false` otherwise         → "expired" (contact support)
   *
   * 4xx is also handled defensively (treated like the matching
   * 200 verdict) in case the BE switches to HTTP-level signalling.
   * 5xx / network errors bubble up so the caller can render the
   * network-error card rather than a misleading expired-link card.
   */
  validateResetToken: async (token) => {
    if (!token) return { status: "expired" };
    if (token === "expired") {
      await delay(VALIDATION_DELAY_MS);
      return { status: "expired" };
    }
    try {
      const data = await api.noAuth.get(
        `/auth/set-password/${encodeURIComponent(token)}/status`,
      );
      // Accept any of the shapes the BE might return.
      // Primary shape (current BE):
      //   { valid: true, issued_at, expires_at, now }
      //   { valid: false, message: "Token has already been used", … }
      //   { valid: false, message: "Invalid token", … }
      // The verdict "already used" is split out of the false bucket
      // so the FE can route the user to sign-in instead of contact-
      // support — see the message-substring check below.
      if (typeof data?.valid === "boolean") {
        if (data.valid) return { status: "valid" };
        const beMsg = String(data?.message || "").toLowerCase();
        if (beMsg.includes("already") && beMsg.includes("used")) {
          return { status: "used" };
        }
        return { status: "expired" };
      }
      // Defensive fallbacks for shape drift — not currently emitted
      // by the BE, but cheap to honour if it ever returns a literal
      // `status` field instead of (or alongside) `valid`.
      const explicit = data?.status;
      if (
        explicit === "valid" ||
        explicit === "expired" ||
        explicit === "used"
      ) {
        return { status: explicit };
      }
      // 200 with no recognised flag → treat as valid.
      return { status: "valid" };
    } catch (err) {
      // expired-link card on a transient outage.
      const status = err?.response?.status;
      if (typeof status === "number" && status >= 400 && status < 500) {
        return { status: "expired" };
      }
      throw err;
    }
  },

  /**
   * Persist the chosen first-time password.
   * `POST /auth/set-password/:token`, body `{ new_password }`.
   * Returns `{ status: "success" }` on completion. Throws on
   * non-2xx (axios) — caller surfaces the server message.
   */
  setPassword: async ({ token, password }) => {
    if (!token) throw new Error("Missing token");
    if (!password) throw new Error("Missing password");
    await api.noAuth.post(`/auth/set-password/${encodeURIComponent(token)}`, {
      new_password: password,
    });
    return { status: "success" };
  },
};

export default passwordService;
